'use client';

import React, { useState } from 'react';

interface Approval {
  id: number;
  property: string;
  host: string;
  location: string;
  type: string;
  image: string;
  imageAlt: string;
  submitted: string;
  price: string;
  rooms: number;
  amenities: string[];
  status: 'pending' | 'approved' | 'rejected';
}

const approvals: Approval[] = [
  {
    id: 1,
    property: 'Safari Tent Kidepo Valley',
    host: 'Moses Kato',
    location: 'Kidepo, Uganda',
    type: 'Tent',
    image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=200&h=140&fit=crop',
    imageAlt: 'Luxury safari tent in Kidepo Valley National Park',
    submitted: 'Aug 21, 2026',
    price: 'UGX 750,000/night',
    rooms: 2,
    amenities: ['WiFi', 'Breakfast', 'Game Drives', 'Private Bathroom'],
    status: 'pending',
  },
  {
    id: 2,
    property: 'Rooftop Penthouse Kampala',
    host: 'Peter Mugisha',
    location: 'Kampala, Uganda',
    type: 'Penthouse',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=200&h=140&fit=crop',
    imageAlt: 'Modern rooftop penthouse with city views in Kampala',
    submitted: 'Aug 20, 2026',
    price: 'UGX 1,800,000/night',
    rooms: 4,
    amenities: ['Pool', 'Gym', 'Concierge', 'Parking', 'WiFi'],
    status: 'pending',
  },
  {
    id: 3,
    property: 'Treehouse Kibale Forest',
    host: 'Ruth Kemigisha',
    location: 'Kibale, Uganda',
    type: 'Treehouse',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=200&h=140&fit=crop',
    imageAlt: 'Elevated treehouse surrounded by Kibale Forest canopy',
    submitted: 'Aug 19, 2026',
    price: 'UGX 950,000/night',
    rooms: 1,
    amenities: ['Guided Walks', 'Breakfast', 'Solar Power'],
    status: 'pending',
  },
  {
    id: 4,
    property: 'Beach Bungalow Ssese Islands',
    host: 'Grace Achieng',
    location: 'Ssese Islands, Uganda',
    type: 'Bungalow',
    image: 'https://images.unsplash.com/photo-1439130490301-25e322d88054?w=200&h=140&fit=crop',
    imageAlt: 'Beachfront bungalow on Ssese Islands Lake Victoria',
    submitted: 'Aug 18, 2026',
    price: 'UGX 680,000/night',
    rooms: 2,
    amenities: ['Beach Access', 'Kayaks', 'WiFi', 'Breakfast'],
    status: 'approved',
  },
];

export default function AdminApprovals() {
  const [items, setItems] = useState<Approval[]>(approvals);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const handleAction = (id: number, action: 'approved' | 'rejected') => {
    setItems((prev) => prev.map((a) => (a.id === id ? { ...a, status: action } : a)));
  };

  const filtered = items.filter((a) => filter === 'all' || a.status === filter);
  const pendingCount = items.filter((a) => a.status === 'pending').length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all flex items-center gap-1.5 ${
                filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
              }`}
            >
              {f}
              {f === 'pending' && pendingCount > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === 'pending' ? 'bg-white/30 text-white' : 'bg-[#4088C6]/10 text-[#4088C6]'}`}>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Approval Cards */}
      <div className="space-y-4">
        {filtered.map((item) => (
          <div key={item.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-start gap-4 p-5">
              <img src={item.image} alt={item.imageAlt} className="w-28 h-20 rounded-xl object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-gray-900">{item.property}</h3>
                    <p className="text-sm text-gray-500">{item.location} · {item.type} · {item.rooms} room{item.rooms > 1 ? 's' : ''}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Host: {item.host} · Submitted {item.submitted}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleAction(item.id, 'approved')}
                          className="px-3 py-1.5 bg-green-500 text-white text-xs font-semibold rounded-lg hover:bg-green-600 transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleAction(item.id, 'rejected')}
                          className="px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {item.status === 'approved' && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">Approved</span>
                    )}
                    {item.status === 'rejected' && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-600">Rejected</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-sm font-bold text-gray-900">{item.price}</span>
                  <div className="flex flex-wrap gap-1">
                    {item.amenities.slice(0, 3).map((a) => (
                      <span key={a} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{a}</span>
                    ))}
                    {item.amenities.length > 3 && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">+{item.amenities.length - 3} more</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 pb-4">
              <button
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                className="text-xs font-semibold text-[#4088C6] hover:text-[#3070a6] transition-colors"
              >
                {expandedId === item.id ? '▲ Hide details' : '▼ View full details'}
              </button>
              {expandedId === item.id && (
                <div className="mt-3 p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs font-semibold text-gray-500 mb-2">All Amenities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {item.amenities.map((a) => (
                      <span key={a} className="text-xs bg-white border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full">{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-gray-500 text-sm">No {filter} approvals</p>
          </div>
        )}
      </div>
    </div>
  );
}
